<template>
  <Dialog
    title="题目绑定"
    :visible.sync="visible"
    width="760px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div v-loading="loading" class="course-question">
      <div class="course-question__header">
        <span class="course-question__duration">
          视频时长：{{ durationText }}
        </span>
      </div>

      <div
        v-for="(item, index) in questionList"
        :key="item._id"
        class="course-question__item"
      >
        <div class="course-question__item-head">
          <span class="course-question__item-title">
            防刷课题目 {{ index + 1 }}
          </span>
          <el-button plain round size="small"
            v-if="editable"
            type="danger"
            @click="removeQuestion(index)"
          >
            删除
          </el-button>
        </div>

        <div class="course-question__time-row">
          <el-time-picker
            v-model="item.time"
            value-format="HH:mm:ss"
            format="HH:mm:ss"
            placeholder="选择时间点"
            :disabled="!editable"
            :picker-options="timePickerOptions"
            style="width: 160px;"
          />
          <span class="course-question__time-tip">不超过视频时长</span>
        </div>

        <el-input
          v-model.trim="item.title"
          type="textarea"
          :autosize="{ minRows: 2, maxRows: 6 }"
          placeholder="请输入题目"
          maxlength="300"
          show-word-limit
          :disabled="!editable"
          class="course-question__title-input"
        />

        <div class="course-question__options">
          <div
            v-for="(option, optIndex) in item.options"
            :key="optIndex"
            class="course-question__option"
          >
            <el-radio
              v-model="item.answer"
              :label="optIndex"
              :disabled="!editable"
            >
              {{ optionLabels[optIndex] }}
            </el-radio>
            <el-input
              v-model.trim="item.options[optIndex]"
              :placeholder="'请输入选项' + optionLabels[optIndex]"
              maxlength="50"
              show-word-limit
              :disabled="!editable"
            />
          </div>
        </div>
      </div>

      <el-button plain round size="small"
        v-if="editable"
        type="primary"
        icon="el-icon-plus"
        class="course-question__add-btn"
        @click="addQuestion"
      >
        添加防刷课题目
      </el-button>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button plain round size="small" @click="handleClose">关 闭</el-button>
      <el-button plain round size="small"
        v-if="editable"
        type="primary"
        :loading="saving"
        @click="handleSave"
      >
        保 存
      </el-button>
    </div>
  </Dialog>
</template>

<script>
import { updateChapterClass, getChapterClassDetail } from "@/api/course";
import { formatDuration } from "@/utils/util";

const OPTION_LABELS = ["A", "B", "C", "D"];

function parseQuestionJson(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function createQuestionItem(payload = {}) {
  return {
    _id:
      payload._id ||
      `q_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    time: payload.time || "00:00:00",
    title: payload.title || "",
    options: normalizeOptions(payload.options),
    answer:
      payload.answer === undefined || payload.answer === null || payload.answer === ""
        ? null
        : Number(payload.answer)
  };
}

function normalizeOptions(options) {
  const list = Array.isArray(options) ? options.slice(0, 4) : [];
  while (list.length < 4) {
    list.push("");
  }
  return list;
}

function timeStrToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== "string") {
    return 0;
  }
  const parts = timeStr.split(":").map(part => Number(part));
  if (parts.length !== 3 || parts.some(part => isNaN(part))) {
    return 0;
  }
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

function secondsToTimeStr(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((total % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(total % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default {
  name: "QuestionForm",
  props: {
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      visible: false,
      classData: {},
      duration: 0,
      questionList: [],
      saving: false,
      loading: false,
      optionLabels: OPTION_LABELS
    };
  },
  computed: {
    durationText() {
      return formatDuration(this.duration || 0);
    },
    maxDurationTime() {
      return secondsToTimeStr(this.duration || 0);
    },
    timePickerOptions() {
      return {
        selectableRange: `00:00:00 - ${this.maxDurationTime}`
      };
    }
  },
  methods: {
    async open(row = {}) {
      if (!row.classId) {
        this.$message.warning("课时信息不完整，无法加载题目");
        return;
      }
      this.visible = true;
      this.loading = true;
      this.questionList = [];
      try {
        const res = await getChapterClassDetail(row.classId);
        const detail = res.data || {};
        this.classData = { ...detail };
        this.duration = Number(detail.classTime) || 0;
        this.questionList = parseQuestionJson(detail.questionJson).map(item =>
          createQuestionItem(item)
        );
      } catch (e) {
        this.handleClose();
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.visible = false;
      this.classData = {};
      this.duration = 0;
      this.questionList = [];
      this.loading = false;
    },
    addQuestion() {
      this.questionList.push(createQuestionItem());
    },
    removeQuestion(index) {
      this.questionList.splice(index, 1);
    },
    validateQuestions() {
      const maxSeconds = Number(this.duration) || 0;
      for (let i = 0; i < this.questionList.length; i++) {
        const item = this.questionList[i];
        const indexText = `第 ${i + 1} 题`;
        if (!item.time) {
          this.$message.warning(`${indexText}请选择出现时间`);
          return false;
        }
        const appearSeconds = timeStrToSeconds(item.time);
        if (maxSeconds > 0 && appearSeconds > maxSeconds) {
          this.$message.warning(`${indexText}出现时间不能超过视频时长`);
          return false;
        }
        if (!item.title || !item.title.trim()) {
          this.$message.warning(`${indexText}请输入题目内容`);
          return false;
        }
        const trimmedOptions = item.options.map(option =>
          (option || "").trim()
        );
        const emptyOption = trimmedOptions.findIndex(option => !option);
        if (emptyOption > -1) {
          this.$message.warning(
            `${indexText}请完善选项 ${OPTION_LABELS[emptyOption]}`
          );
          return false;
        }
        const duplicateIndex = trimmedOptions.findIndex(
          (option, optIndex) => trimmedOptions.indexOf(option) !== optIndex
        );
        if (duplicateIndex > -1) {
          this.$message.warning(
            `${indexText}选项 ${OPTION_LABELS[duplicateIndex]} 与其他选项重复，请修改`
          );
          return false;
        }
        if (
          item.answer === undefined ||
          item.answer === null ||
          item.answer < 0 ||
          item.answer > 3
        ) {
          this.$message.warning(`${indexText}请选择正确答案`);
          return false;
        }
      }
      return true;
    },
    buildQuestionPayload() {
      return this.questionList.map(item => ({
        time: item.time,
        title: item.title.trim(),
        options: item.options.map(option => option.trim()),
        answer: item.answer
      }));
    },
    async handleSave() {
      if (this.saving) return;
      if (!this.validateQuestions()) {
        return;
      }

      const questionJson = JSON.stringify(this.buildQuestionPayload());
      this.saving = true;
      try {
        await updateChapterClass({
          ...this.classData,
          questionJson
        });
        this.classData.questionJson = questionJson;
        this.$message.success("防刷课题目保存成功");
        this.handleClose();
      } catch (e) {
        // axios 拦截器已提示
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
.course-question {
  &__header {
    margin-bottom: 16px;
  }

  &__duration {
    color: #909399;
    font-size: 13px;
  }

  &__item {
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 12px;
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__item-title {
    font-weight: 600;
    color: #303133;
  }

  &__time-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__time-tip {
    color: #909399;
    font-size: 12px;
  }

  &__title-input {
    margin-bottom: 12px;
  }

  &__options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 24px;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 8px;

    /deep/ .el-radio {
      margin-right: 0;
      width: 28px;
      flex-shrink: 0;
    }

    /deep/ .el-radio__label {
      padding-left: 4px;
    }
  }

  &__add-btn {
    margin-top: 4px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/CourseEdit/components/QuestionForm.vue